/**
 * Verve AI Labs — GST System Instructions & Statutory Intelligence Engine
 * apps/gst-portal/src/services/gstSystemInstructions.ts
 *
 * Single Source of Truth for system prompts, business logic rules, statutory date resolution,
 * and post-extraction validation for Indian GST invoices, debit notes, and receipts.
 */

import { ExtractedGstInvoice, ItcEligibility } from '../types/gst';

export type GstRuleCategory =
  | 'DATE_AND_PERIOD'
  | 'TAX_MECHANISM_FORWARD_VS_RCM'
  | 'RULE_33_REIMBURSEMENT_NON_GST'
  | 'DOCUMENT_CLASSIFICATION'
  | 'LINE_ITEM_AND_MATH'
  | 'PARTY_IDENTIFICATION';

export interface GstSystemRule {
  id: string;
  category: GstRuleCategory;
  priority: 'MANDATORY' | 'STATUTORY';
  title: string;
  description: string;
  ruleInstruction: string;
}

/**
 * Segregated list of instructions that act as the system prompt for AI extraction
 * and deterministic business logic validation.
 * As new invoice formats or edge cases are encountered, add new entries to this list.
 */
export const GST_SYSTEM_INSTRUCTIONS: GstSystemRule[] = [
  // -------------------------------------------------------------------------
  // SECTION 1: STATUTORY DATE, FINANCIAL YEAR & RETURN PERIOD
  // -------------------------------------------------------------------------
  {
    id: "RULE_DATE_TAX_PERIOD_SYNC",
    category: "DATE_AND_PERIOD",
    priority: "MANDATORY",
    title: "Document Date Strictly Dictates Financial Year and Return Period (Month)",
    description: "In Indian GST statutory accounting, filing obligations and ledger indexing are strictly determined by the invoice date, NEVER by the month currently selected in the UI header.",
    ruleInstruction: `
1. Extract the exact printed document date and format strictly as 'YYYY-MM-DD'.
2. The document date determines the statutory Financial Year (April 1 to March 31):
   - For dates between April 1 and December 31 of Year YYYY: Financial Year is 'YYYY-(YY+1)' (e.g., date 2026-10-22 is strictly in FY '2026-27').
   - For dates between January 1 and March 31 of Year YYYY: Financial Year is '(YYYY-1)-YY' (e.g., date 2026-02-15 is strictly in FY '2025-26').
3. The document date determines the statutory Return Filing Period (Month):
   - Month 01 -> January, Month 02 -> February, Month 03 -> March, Month 04 -> April,
   - Month 05 -> May, Month 06 -> June, Month 07 -> July, Month 08 -> August,
   - Month 09 -> September, Month 10 -> October, Month 11 -> November, Month 12 -> December.
4. An invoice dated 22/10/2026 MUST be filed under October (FY 2026-27). Never file it into July or any other month!
`
  },

  // -------------------------------------------------------------------------
  // SECTION 2: FORWARD CHARGE VS REVERSE CHARGE (RCM)
  // -------------------------------------------------------------------------
  {
    id: "RULE_FORWARD_VS_RCM",
    category: "TAX_MECHANISM_FORWARD_VS_RCM",
    priority: "STATUTORY",
    title: "Tax Charged by Supplier is Forward Charge (Section 16 Eligible ITC), Never RCM",
    description: "When an Indian registered supplier charges GST on a tax invoice, the tax is being paid by the vendor under forward charge. The buyer claims Input Tax Credit (ITC) under Section 16(2). RCM applies only when the vendor does NOT charge tax and tax must be paid in cash by the recipient under Section 9(3).",
    ruleInstruction: `
1. FORWARD CHARGE RULE: If an invoice is issued by a registered Indian supplier with a valid 15-character GSTIN, and the invoice CHARGES GST (CGST/SGST or IGST > 0, Total Tax > 0):
   - It is FORWARD CHARGE!
   - reverse_charge_applicable MUST BE FALSE.
   - itc_eligibility MUST BE 'ELIGIBLE' (standard B2B Input Tax Credit under Section 16(2)), unless specifically blocked under Section 17(5) (e.g., food, cab rides, personal gifts).
   - Even if the SAC code is 9982 (legal consultancy / company registration) or 9983 (professional consulting), if the vendor charged 18% GST (e.g., Startup Movers charging ₹270 CGST + ₹270 SGST on ₹3,000), it is FORWARD CHARGE! The recipient does NOT pay under RCM, the recipient claims standard ITC!
2. REVERSE CHARGE (RCM) CONDITIONS: Section 9(3) / 5(3) RCM applies ONLY when:
   - Foreign SaaS / Cloud bill without Indian GSTIN (OpenAI, AWS, GitHub, Vercel, Stripe).
   - The document explicitly states "Reverse Charge Applicable: YES" or "Tax Payable on Reverse Charge: YES" and NO forward charge tax was charged by the supplier.
   - Individual advocate court representation where no GST is billed by the advocate.
   - GTA (Goods Transport Agency) freight consignment note where transporter did not charge 12% forward charge.
3. CRITICAL: NEVER classify a domestic invoice that already collects 18% GST (e.g., ₹540 tax on ₹3,000) as RCM!
`
  },

  // -------------------------------------------------------------------------
  // SECTION 3: ZERO-TAX & PURE REIMBURSEMENT (RULE 33)
  // -------------------------------------------------------------------------
  {
    id: "RULE_33_REIMBURSEMENT_NON_GST",
    category: "RULE_33_REIMBURSEMENT_NON_GST",
    priority: "STATUTORY",
    title: "Pure Agent Reimbursements and Zero-GST Documents (Rule 33 CGST Rules)",
    description: "Government fees (MCA/ROC filing fees like INC20A, INC22, ADT-1), stamp duty, and pure agent reimbursements have 0% GST. They carry ₹0 tax liability and ₹0 ITC.",
    ruleInstruction: `
1. If the document is a DEBIT NOTE or REIMBURSEMENT NOTE for government fees or pure agent out-of-pocket expenses where NO tax is charged:
   - All tax rates (CGST, SGST, IGST, Cess) MUST be 0.00%.
   - Total tax MUST be 0.00.
   - is_reimbursement_or_pure_agent MUST be true.
   - itc_eligibility MUST be 'INELIGIBLE' (Zero GST / Pure Reimbursement).
   - reverse_charge_applicable MUST be false.
2. Do not invent or accumulate false Input Tax Credit for government fee reimbursements.
`
  },

  // -------------------------------------------------------------------------
  // SECTION 4: DOCUMENT CLASSIFICATION
  // -------------------------------------------------------------------------
  {
    id: "RULE_DOC_TYPE_DISCRIMINATION",
    category: "DOCUMENT_CLASSIFICATION",
    priority: "MANDATORY",
    title: "Explicit Document Header Classification",
    description: "Identify whether the document is a Tax Invoice, Debit Note, Credit Note, or Bill of Supply.",
    ruleInstruction: `
1. Check the main printed title:
   - "TAX INVOICE" -> document_type: "INVOICE"
   - "DEBIT NOTE" -> document_type: "DEBIT_NOTE"
   - "CREDIT NOTE" -> document_type: "CREDIT_NOTE"
   - "BILL OF SUPPLY" -> document_type: "BILL_OF_SUPPLY"
2. Extract the printed document number exactly (e.g., 'TI/UR/26-27/2055' or 'DN/UR/26-27/2659'). Never alter or shorten document numbers.
`
  },

  // -------------------------------------------------------------------------
  // SECTION 5: LINE ITEM MATH & RATE INTEGRITY
  // -------------------------------------------------------------------------
  {
    id: "RULE_STRICT_LINE_ITEM_MATH",
    category: "LINE_ITEM_AND_MATH",
    priority: "MANDATORY",
    title: "Deterministic Line-Item Rates and Values",
    description: "Extract the exact quantities, unit rates, taxable amounts, and tax rates without hallucination.",
    ruleInstruction: `
1. Extract each line item with its printed description, HSN/SAC code, quantity, unit rate, and taxable value.
2. If rates are 9% CGST + 9% SGST, record cgst_rate: 9, sgst_rate: 9, igst_rate: 0.
3. If interstate supply, record igst_rate: 18 (or appropriate printed rate), cgst_rate: 0, sgst_rate: 0.
4. Total invoice value must strictly equal Taxable Value + CGST + SGST + IGST + Cess.
`
  },

  // -------------------------------------------------------------------------
  // SECTION 6: PARTY IDENTIFICATION & GSTIN INTEGRITY
  // -------------------------------------------------------------------------
  {
    id: "RULE_PARTY_GSTIN_INTEGRITY",
    category: "PARTY_IDENTIFICATION",
    priority: "MANDATORY",
    title: "Supplier and Buyer Identification",
    description: "Capture exact trade name, registered legal name, and 15-character GSTIN.",
    ruleInstruction: `
1. Supplier GSTIN must be validated as 15 characters (e.g., '09AAUCS0938E1ZZ' for Startup Movers in Uttar Pradesh).
2. Supplier Name must reflect the legal trade name printed on the invoice header.
3. Place of Supply must identify the 2-digit state code or state name (e.g. '09 Uttar Pradesh').
`
  }
];

/**
 * Builds the comprehensive Gemini 3.8 Flash Vision extraction prompt
 * compiled from the segregated instruction list.
 */
export function buildGeminiExtractionPrompt(): string {
  const instructionsFormatted = GST_SYSTEM_INSTRUCTIONS.map((rule, idx) => {
    return `### [RULE ${idx + 1}: ${rule.id}] ${rule.title} (${rule.category})\n${rule.ruleInstruction.trim()}`;
  }).join('\n\n');

  return `
You are an expert Indian GST Tax Invoice & Ingestion Auditor for Verve AI Labs.
Extract the structured accounting and statutory tax fields from this document with 100% precision.

=== MANDATORY SYSTEM INSTRUCTIONS & AUDIT RULES ===
${instructionsFormatted}

=== TARGET OUTPUT JSON SCHEMA ===
Return ONLY valid JSON matching this exact structure:
{
  "document_type": "INVOICE or DEBIT_NOTE or CREDIT_NOTE or BILL_OF_SUPPLY",
  "invoice_number": "Raw invoice or debit note number as printed",
  "invoice_date": "YYYY-MM-DD",
  "supplier_name": "Full legal or trade name of vendor",
  "supplier_gstin": "15-char GSTIN string or empty if unregistered",
  "buyer_name": "Full legal name of buyer if present",
  "buyer_gstin": "15-char recipient GSTIN string if present",
  "place_of_supply": "2-digit state code or State name (e.g. '09 Uttar Pradesh')",
  "reverse_charge_applicable": false,
  "is_reimbursement_or_pure_agent": false,
  "invoice_total": 0.00,
  "total_taxable_value": 0.00,
  "total_tax": 0.00,
  "line_items": [
    {
      "description": "Item description",
      "hsn_sac_code": "HSN or SAC code",
      "qty": 1,
      "unit_price": 0.00,
      "taxable_value": 0.00,
      "cgst_rate": 0,
      "sgst_rate": 0,
      "igst_rate": 0,
      "cess_rate": 0
    }
  ]
}
`.trim();
}

/**
 * Resolves Financial Year and Return Period (Month) from an invoice date (YYYY-MM-DD or DD/MM/YYYY)
 * In Indian statutory GST law:
 * - Months are April (04) through March (03).
 * - If month >= 4 (April - December): FY is Year-(Year+1). e.g., Oct 2026 -> FY 2026-27, Period October.
 * - If month <= 3 (January - March): FY is (Year-1)-Year. e.g., Feb 2026 -> FY 2025-26, Period February.
 */
export function resolveStatutoryTaxPeriod(dateString: string | undefined | null): {
  financialYear: string;
  taxPeriod: string;
  parsedDate: string; // YYYY-MM-DD
} {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  if (!dateString) {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const fy = month >= 4 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
    return {
      financialYear: fy,
      taxPeriod: months[now.getMonth()],
      parsedDate: now.toISOString().split('T')[0]
    };
  }

  let y = 0;
  let m = 0;
  let d = 0;

  const clean = dateString.trim();

  if (clean.includes('/')) {
    const parts = clean.split('/');
    if (parts[0].length === 4) {
      // YYYY/MM/DD
      y = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      d = parseInt(parts[2], 10);
    } else {
      // DD/MM/YYYY
      d = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      y = parseInt(parts[2], 10);
    }
  } else if (clean.includes('-')) {
    const parts = clean.split('-');
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      y = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      d = parseInt(parts[2], 10);
    } else {
      // DD-MM-YYYY
      d = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      y = parseInt(parts[2], 10);
    }
  }

  if (!y || !m || isNaN(y) || isNaN(m)) {
    const now = new Date();
    y = now.getFullYear();
    m = now.getMonth() + 1;
  }

  const taxPeriod = months[m - 1] || "July";

  let financialYear = "";
  if (m >= 4) {
    financialYear = `${y}-${(y + 1).toString().slice(-2)}`;
  } else {
    financialYear = `${y - 1}-${y.toString().slice(-2)}`;
  }

  const formattedDate = `${y}-${String(m).padStart(2, '0')}-${String(d || 1).padStart(2, '0')}`;

  return {
    financialYear,
    taxPeriod,
    parsedDate: formattedDate
  };
}

/**
 * Evaluates and refines extracted invoice against all statutory system instructions
 */
export function applyGstSystemInstructions(
  rawInvoice: Partial<ExtractedGstInvoice>
): {
  financialYear: string;
  taxPeriod: string;
  invoiceDate: string;
  itcEligibility: ItcEligibility;
  reverseChargeApplicable: boolean;
  aiComplianceNote: string;
  isReimbursementOrPureAgent: boolean;
} {
  const period = resolveStatutoryTaxPeriod(rawInvoice.invoice_date);
  const lineItems = rawInvoice.line_items || [];
  const supplierGstin = (rawInvoice.supplier_gstin || "").trim().toUpperCase();
  const supplierName = (rawInvoice.supplier_name || "").toLowerCase();

  const totalTax = rawInvoice.total_tax !== undefined 
    ? rawInvoice.total_tax 
    : lineItems.reduce((acc, l) => acc + (l.cgst_amount || 0) + (l.sgst_amount || 0) + (l.igst_amount || 0), 0);

  const hasForwardChargeTax = totalTax > 0.001 && lineItems.some(l => 
    (l.cgst_rate || 0) > 0 || (l.igst_rate || 0) > 0 || 
    (l.cgst_amount || 0) > 0 || (l.igst_amount || 0) > 0
  );

  const isDomesticRegisteredSupplier = supplierGstin.length === 15 && !supplierGstin.startsWith("99");

  // 1. RULE 33: ZERO-GST / PURE REIMBURSEMENT
  const allZeroRates = lineItems.length > 0 && lineItems.every(l => 
    (l.cgst_rate || 0) === 0 && (l.sgst_rate || 0) === 0 && (l.igst_rate || 0) === 0
  );
  const isZeroTax = totalTax <= 0.001 || allZeroRates || Boolean(rawInvoice.is_reimbursement_or_pure_agent);

  if (isZeroTax && (rawInvoice.invoice_total || 0) > 0) {
    return {
      financialYear: period.financialYear,
      taxPeriod: period.taxPeriod,
      invoiceDate: period.parsedDate,
      itcEligibility: 'INELIGIBLE',
      reverseChargeApplicable: false,
      isReimbursementOrPureAgent: true,
      aiComplianceNote: 'Zero-GST / Pure Reimbursement [Rule 33 CGST Rules]: No GST charged on this document (₹0.00 tax). Not eligible for Input Tax Credit (ITC), and no reverse charge applies.'
    };
  }

  // 2. FOREIGN SAAS / IMPORT OF SERVICES (RCM)
  const foreignSaaSKeywords = [
    "openai", "chatgpt", "aws", "amazon web services", "github", 
    "google cloud", "vercel", "supabase", "stripe", "notion", 
    "figma", "anthropic", "slack", "zoom video", "digitalocean", "cloudflare"
  ];
  const isForeignVendor = foreignSaaSKeywords.some(v => supplierName.includes(v));
  const hasNoIndianGstin = !supplierGstin || supplierGstin.length !== 15 || supplierGstin.startsWith("99");

  if ((isForeignVendor && hasNoIndianGstin) || (rawInvoice.category === 'EXPENSE_RECEIPT' && isForeignVendor)) {
    return {
      financialYear: period.financialYear,
      taxPeriod: period.taxPeriod,
      invoiceDate: period.parsedDate,
      itcEligibility: 'RCM_APPLICABLE',
      reverseChargeApplicable: true,
      isReimbursementOrPureAgent: false,
      aiComplianceNote: `RCM Applicable [Section 9(3) CGST / Section 5(3) IGST Act]: Foreign SaaS / Cloud subscription from ${rawInvoice.supplier_name || "overseas vendor"} without Indian GSTIN. Company must pay 18% IGST under RCM and claim 100% ITC in Table 4(A)(2).`
    };
  }

  // 3. SECTION 17(5) BLOCKED CREDITS (Food, beverages, passenger cars, personal expenses)
  const blockedKeywords = ["food", "beverage", "restaurant", "catering", "swiggy", "zomato", "starbucks", "motor vehicle", "car rental", "uber", "ola", "club membership"];
  const fullText = (supplierName + " " + lineItems.map(l => `${l.description} ${l.hsn_sac_code}`).join(" ")).toLowerCase();
  const isBlocked = blockedKeywords.some(k => fullText.includes(k)) || lineItems.some(l => (l.hsn_sac_code || "").startsWith("9963") || (l.hsn_sac_code || "").startsWith("8703"));

  if (isBlocked) {
    return {
      financialYear: period.financialYear,
      taxPeriod: period.taxPeriod,
      invoiceDate: period.parsedDate,
      itcEligibility: 'BLOCKED_17_5',
      reverseChargeApplicable: false,
      isReimbursementOrPureAgent: false,
      aiComplianceNote: 'Blocked Credit [Section 17(5) CGST Act]: Input Tax Credit is blocked on food, motor vehicles, or personal consumption. Cannot be claimed in GSTR-3B Table 4(A).'
    };
  }

  // 4. FORWARD CHARGE TAX INVOICE FROM REGISTERED SUPPLIER
  // If supplier charged tax on invoice and has valid Indian GSTIN, it is FORWARD CHARGE!
  // It is ELIGIBLE under Section 16(2), NOT RCM!
  if (hasForwardChargeTax && isDomesticRegisteredSupplier && !rawInvoice.reverse_charge_applicable) {
    return {
      financialYear: period.financialYear,
      taxPeriod: period.taxPeriod,
      invoiceDate: period.parsedDate,
      itcEligibility: 'ELIGIBLE',
      reverseChargeApplicable: false,
      isReimbursementOrPureAgent: false,
      aiComplianceNote: `Forward Charge Tax Invoice [Section 16(2) CGST Act]: ${rawInvoice.supplier_name || 'Supplier'} charged GST on forward charge. Recipient is entitled to claim Input Tax Credit in GSTR-3B Table 4(A)(5). Not subject to Reverse Charge.`
    };
  }

  // 5. EXPLICIT RCM (Supplier indicated RCM or no tax charged on domestic RCM supply)
  if (rawInvoice.reverse_charge_applicable) {
    return {
      financialYear: period.financialYear,
      taxPeriod: period.taxPeriod,
      invoiceDate: period.parsedDate,
      itcEligibility: 'RCM_APPLICABLE',
      reverseChargeApplicable: true,
      isReimbursementOrPureAgent: false,
      aiComplianceNote: "RCM Applicable: Supplier explicitly marked Reverse Charge on invoice."
    };
  }

  // Default: Standard Eligible Section 16(2) ITC
  return {
    financialYear: period.financialYear,
    taxPeriod: period.taxPeriod,
    invoiceDate: period.parsedDate,
    itcEligibility: 'ELIGIBLE',
    reverseChargeApplicable: false,
    isReimbursementOrPureAgent: false,
    aiComplianceNote: "Eligible ITC [Section 16(2) CGST Act]: Standard business input tax credit. Claimable in GSTR-3B Table 4(A)(5)."
  };
}
